import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, Paper, Grid, Container, Chip } from '@mui/material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../hooks';
import { useDocumentTitle } from '../hooks/core';
import {
  Toll as CreditIcon,
  CheckCircle as CheckIcon,
  Palette as ThemeIcon,
  SportsSoccer as SportsIcon,
  EmojiEvents as TrophyIcon,
  People as PeopleIcon,
  Dashboard as GridIcon,
  Security as SecureIcon,
  Share as ShareIcon,
  CardGiftcard as GiftIcon,
  HelpOutline as HelpIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';
import { productsApi, type PaymentProduct } from '../api/checkout';
import { publicPaymentApi } from '../api/billing';
import { referralApi, type ReferralRewardTier } from '../api/referral';
import { trackHomepageClick } from '../lib/analytics';
import { CreditPackCard, CreditPackSkeleton } from '../components/home';
import { buildAuthUrl, isBestValue } from '../utils/pricing';

export const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [creditPacks, setCreditPacks] = useState<PaymentProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [freeCredits, setFreeCredits] = useState<number>(2); // Default to 2
  const [referralTiers, setReferralTiers] = useState<ReferralRewardTier[]>([]);

  useDocumentTitle(undefined);

  // Helper to track and navigate
  const trackAndNavigate = (buttonName: string, section: string, destination: string) => {
    trackHomepageClick(buttonName, section, destination);
    navigate(destination);
  };

  // Fetch credit packs, payment config, and referral tiers on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch all data in parallel
        const [products, paymentConfig, tiers] = await Promise.all([
          productsApi.listCreditPacks(),
          publicPaymentApi.getPublicConfig(),
          referralApi.getTiers(),
        ]);

        // Sort credit packs by credits ascending
        const sorted = products.sort((a, b) => {
          const aCredits = a.metadata?.boardCredits || 0;
          const bCredits = b.metadata?.boardCredits || 0;
          return aCredits - bCredits;
        });
        setCreditPacks(sorted);

        // Set free credits from config
        setFreeCredits(paymentConfig.freeCreditsForNewUsers);

        // Set referral tiers (sorted by minReferrals)
        const sortedTiers = tiers.sort((a, b) => a.minReferrals - b.minReferrals);
        setReferralTiers(sortedTiers);
      } catch (error) {
        console.error('Failed to fetch homepage data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          textAlign: 'center',
          py: 8,
          background: 'linear-gradient(135deg, #1976d2 0%, #7c3aed 100%)',
          color: 'white',
          borderRadius: 2,
          mb: 6,
        }}
      >
        <Typography variant="h2" component="h1" gutterBottom>
          SquareUp
        </Typography>
        <Typography variant="h5" sx={{ mb: 4, opacity: 0.9 }}>
          The ultimate sports squares board manager
        </Typography>
        {isAuthenticated ? (
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => trackAndNavigate('view_my_boards', 'hero', '/boards')}
              sx={{ backgroundColor: 'white', color: 'primary.main' }}
            >
              View My Boards
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => trackAndNavigate('create_new_board', 'hero', '/create-board')}
              sx={{ borderColor: 'white', color: 'white' }}
            >
              Create New Board
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => trackAndNavigate('get_started_free', 'hero', '/register')}
              sx={{ backgroundColor: 'white', color: 'primary.main' }}
            >
              Get Started Free
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => trackAndNavigate('login', 'hero', '/login')}
              sx={{ borderColor: 'white', color: 'white' }}
            >
              Login
            </Button>
          </Box>
        )}
      </Box>

      {/* What is Squares? Section */}
      <Container sx={{ mb: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            What is a Squares Pool?
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto' }}>
            A squares pool is a fun, social prediction game tied to sports scores. It's the perfect way to make any game more exciting with friends and family!
          </Typography>
        </Box>

        {/* Visual Grid Example */}
        <Grid container spacing={4} alignItems="center" sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                border: 2,
                borderColor: 'primary.light',
                borderRadius: 2,
                bgcolor: 'grey.50',
              }}
            >
              <Typography variant="subtitle2" fontWeight="bold" color="primary.main" gutterBottom>
                The 10x10 Grid
              </Typography>
              {/* Mini Grid Visualization */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Box>
                  {/* Header row with team name */}
                  <Box sx={{ display: 'flex' }}>
                    <Box sx={{ width: 32, height: 24 }} />
                    <Box sx={{ width: 24 }} />
                    <Box
                      sx={{
                        flex: 1,
                        display: 'flex',
                        justifyContent: 'center',
                        bgcolor: 'primary.main',
                        color: 'white',
                        fontSize: 11,
                        fontWeight: 'bold',
                        py: 0.5,
                        borderTopLeftRadius: 4,
                        borderTopRightRadius: 4,
                        minWidth: 180,
                      }}
                    >
                      HOME TEAM
                    </Box>
                  </Box>
                  {/* Grid with numbers */}
                  <Box sx={{ display: 'flex' }}>
                    {/* Away team label */}
                    <Box
                      sx={{
                        width: 32,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        bgcolor: 'error.main',
                        color: 'white',
                        fontSize: 10,
                        fontWeight: 'bold',
                        writingMode: 'vertical-rl',
                        transform: 'rotate(180deg)',
                        borderTopLeftRadius: 4,
                        borderBottomLeftRadius: 4,
                      }}
                    >
                      AWAY TEAM
                    </Box>
                    {/* Numbers column */}
                    <Box>
                      <Box sx={{ height: 22 }} />
                      {[7, 2, 4, 0, 8, 3, 9, 1, 6, 5].map((num, i) => (
                        <Box
                          key={i}
                          sx={{
                            width: 24,
                            height: 18,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'grey.200',
                            fontSize: 10,
                            fontWeight: 600,
                            borderBottom: 1,
                            borderColor: 'grey.300',
                          }}
                        >
                          {num}
                        </Box>
                      ))}
                    </Box>
                    {/* Main grid */}
                    <Box>
                      {/* Numbers row */}
                      <Box sx={{ display: 'flex' }}>
                        {[3, 0, 7, 5, 1, 9, 2, 8, 4, 6].map((num, j) => (
                          <Box
                            key={j}
                            sx={{
                              width: 18,
                              height: 22,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: 'grey.200',
                              fontSize: 10,
                              fontWeight: 600,
                              borderRight: 1,
                              borderColor: 'grey.300',
                            }}
                          >
                            {num}
                          </Box>
                        ))}
                      </Box>
                      {/* Grid squares */}
                      {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
                        <Box key={i} sx={{ display: 'flex' }}>
                          {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((j) => {
                            // Highlight the winning square (score 17-24, last digits 7 and 4)
                            // Home numbers: [3,0,7,5,1,9,2,8,4,6] - col 2 has "7"
                            // Away numbers: [7,2,4,0,8,3,9,1,6,5] - row 2 has "4"
                            const isWinner = i === 2 && j === 2;
                            const colors = ['#e3f2fd', '#fff3e0', '#e8f5e9', '#fce4ec', '#f3e5f5'];
                            const claimedColor = (i + j) % 5;
                            return (
                              <Box
                                key={j}
                                sx={{
                                  width: 18,
                                  height: 18,
                                  bgcolor: isWinner ? '#4caf50' : colors[claimedColor],
                                  border: 1,
                                  borderColor: isWinner ? '#2e7d32' : 'grey.300',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: 8,
                                  color: isWinner ? 'white' : 'text.secondary',
                                  fontWeight: isWinner ? 'bold' : 400,
                                }}
                              >
                                {isWinner ? 'WIN' : ''}
                              </Box>
                            );
                          })}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              </Box>
              <Typography variant="caption" color="text.secondary" display="block" textAlign="center" sx={{ mt: 2 }}>
                Score: Home 17, Away 24 - Last digits 7 and 4 meet at the winning square!
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="h6" gutterBottom>
              How It Works
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {[
                { step: '1', title: 'Claim Your Squares', desc: 'Pick any available squares on the 10x10 grid. Each square has a small entry fee.' },
                { step: '2', title: 'Numbers Get Randomized', desc: 'Once all squares are claimed, random numbers (0-9) are assigned to rows and columns.' },
                { step: '3', title: 'Watch the Game', desc: 'At the end of each quarter, the last digit of each team\'s score determines the winner.' },
                { step: '4', title: 'Win Prizes!', desc: 'If your square matches the score\'s last digits, you win that quarter\'s payout!' },
              ].map((item) => (
                <Box key={item.step} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      flexShrink: 0,
                    }}
                  >
                    {item.step}
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {item.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.desc}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>

        {/* Example Scenario */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            bgcolor: 'success.50',
            border: 1,
            borderColor: 'success.200',
            borderRadius: 2,
            mb: 4,
          }}
        >
          <Grid container spacing={3} alignItems="center">
            <Grid size={{ xs: 12, md: 8 }}>
              <Typography variant="h6" gutterBottom color="success.dark">
                <TrophyIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                Example: Finding the Winner
              </Typography>
              <Typography variant="body2" paragraph>
                Let's say the score at halftime is <strong>Chiefs 17, Eagles 24</strong>:
              </Typography>
              <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Chiefs score:</Typography>
                  <Typography variant="body1"><strong>17</strong> - last digit is <Chip label="7" size="small" color="primary" /></Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Eagles score:</Typography>
                  <Typography variant="body1"><strong>24</strong> - last digit is <Chip label="4" size="small" color="error" /></Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Winner:</Typography>
                  <Typography variant="body1">The square where <Chip label="7" size="small" color="primary" /> and <Chip label="4" size="small" color="error" /> meet!</Typography>
                </Box>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }} sx={{ textAlign: 'center' }}>
              <Button
                component={RouterLink}
                to="/support/game-mechanics"
                variant="contained"
                color="success"
                size="large"
                endIcon={<ArrowIcon />}
                onClick={() => {
                  trackHomepageClick('learn_more', 'what_is_squares', '/support/game-mechanics');
                  window.scrollTo(0, 0);
                }}
              >
                Learn More
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Quick Benefits */}
        <Grid container spacing={2}>
          {[
            { icon: <PeopleIcon />, title: 'Social & Fun', desc: 'Perfect for watch parties, office pools, and family gatherings' },
            { icon: <SecureIcon />, title: 'Fair & Random', desc: 'Numbers are randomly assigned so every square has equal chances' },
            { icon: <HelpIcon />, title: 'Easy to Learn', desc: 'No sports knowledge needed - anyone can play and win!' },
          ].map((benefit, index) => (
            <Grid size={{ xs: 12, sm: 4 }} key={index}>
              <Paper elevation={0} sx={{ p: 2, textAlign: 'center', border: 1, borderColor: 'divider', height: '100%' }}>
                <Box sx={{ color: 'primary.main', mb: 1 }}>{benefit.icon}</Box>
                <Typography variant="subtitle2" fontWeight="bold">{benefit.title}</Typography>
                <Typography variant="body2" color="text.secondary">{benefit.desc}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Pricing Section */}
      <Container sx={{ mb: 8 }}>
        <Typography variant="h4" align="center" gutterBottom sx={{ mb: 2 }}>
          Simple Pricing
        </Typography>
        <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 4 }}>
          Start with {freeCredits} free board{freeCredits !== 1 ? 's' : ''}, then pay only for what you need
        </Typography>

        {/* Free Credits Banner */}
        <Paper
          sx={{
            p: 3,
            mb: 4,
            background: 'linear-gradient(135deg, #4caf50 0%, #81c784 100%)',
            color: 'white',
            borderRadius: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 3,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  borderRadius: '50%',
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CreditIcon sx={{ fontSize: 40 }} />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight="bold">
                  Start Free with {freeCredits} Board Credit{freeCredits !== 1 ? 's' : ''}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Every new account gets {freeCredits} free credit{freeCredits !== 1 ? 's' : ''}. No credit card required.
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap', justifyContent: { xs: 'center', md: 'flex-end' } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckIcon fontSize="small" />
                <Typography variant="body2">Full features</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckIcon fontSize="small" />
                <Typography variant="body2">Unlimited participants</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckIcon fontSize="small" />
                <Typography variant="body2">Never expires</Typography>
              </Box>
              {!isAuthenticated && (
                <Button
                  variant="contained"
                  onClick={() => trackAndNavigate('get_started_free', 'pricing_banner', '/register')}
                  sx={{
                    bgcolor: 'white',
                    color: 'success.main',
                    '&:hover': { bgcolor: 'grey.100' },
                  }}
                >
                  Get Started Free
                </Button>
              )}
            </Box>
          </Box>
        </Paper>

        {/* Credit Packs Header */}
        <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 3 }}>
          Need more boards? Choose a credit pack:
        </Typography>

        <Grid container spacing={4} justifyContent="center">
          {/* Dynamic Credit Packs from API */}
          {loading ? (
            // Show skeletons while loading
            <>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <CreditPackSkeleton />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <CreditPackSkeleton />
              </Grid>
            </>
          ) : creditPacks.length > 0 ? (
            // Show actual credit packs
            creditPacks.map((product) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={product.id}>
                <CreditPackCard
                  product={product}
                  isBestValue={isBestValue(product, creditPacks)}
                  isAuthenticated={isAuthenticated}
                  onNavigate={navigate}
                  onTrack={(buttonName, destination) => trackHomepageClick(buttonName, 'pricing', destination)}
                />
              </Grid>
            ))
          ) : (
            // Fallback if no credit packs found
            <Grid size={12}>
              <Typography variant="body2" color="text.secondary" align="center">
                Credit packs coming soon!
              </Typography>
            </Grid>
          )}
        </Grid>

        {/* Referral Section */}
        {referralTiers.length > 0 && (
          <Paper
            sx={{
              mt: 4,
              p: 3,
              background: 'linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)',
              color: 'white',
              borderRadius: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: 'center',
                gap: 3,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    borderRadius: '50%',
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <GiftIcon sx={{ fontSize: 40 }} />
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight="bold">
                    Earn Free Boards with Referrals
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Share SquareUp with friends and earn free board credits when they sign up!
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', justifyContent: { xs: 'center', md: 'flex-end' }, flexGrow: 1 }}>
                {referralTiers.slice(0, 3).map((tier) => (
                  <Box
                    key={tier.id}
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.15)',
                      borderRadius: 2,
                      px: 2,
                      py: 1,
                      textAlign: 'center',
                      minWidth: 100,
                    }}
                  >
                    <Typography variant="h6" fontWeight="bold">
                      +{tier.creditsAwarded}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      {tier.minReferrals} referral{tier.minReferrals !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                ))}
                {isAuthenticated ? (
                  <Button
                    variant="contained"
                    onClick={() => trackAndNavigate('get_referral_link', 'referral', '/profile/referrals')}
                    startIcon={<ShareIcon />}
                    sx={{
                      bgcolor: 'white',
                      color: 'primary.main',
                      '&:hover': { bgcolor: 'grey.100' },
                    }}
                  >
                    Get My Link
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={() => trackAndNavigate('sign_up_to_refer', 'referral', buildAuthUrl('/register', '/profile/referrals'))}
                    sx={{
                      bgcolor: 'white',
                      color: 'primary.main',
                      '&:hover': { bgcolor: 'grey.100' },
                    }}
                  >
                    Sign Up to Refer
                  </Button>
                )}
              </Box>
            </Box>
          </Paper>
        )}
      </Container>

      {/* Theme Store Section */}
      <Box
        sx={{
          py: 6,
          px: 4,
          mb: 8,
          bgcolor: 'grey.50',
          borderRadius: 2,
        }}
      >
        <Container>
          <Grid container spacing={4} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <ThemeIcon color="primary" sx={{ fontSize: 40 }} />
                <Typography variant="h4">
                  Theme Store
                </Typography>
              </Box>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Customize your boards with premium themes. Choose from a variety of
                sports-specific designs, team colors, and seasonal themes to make
                your boards stand out.
              </Typography>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CheckIcon color="success" fontSize="small" />
                  <Typography variant="body2">NFL, NBA, MLB, NHL, and more</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CheckIcon color="success" fontSize="small" />
                  <Typography variant="body2">Seasonal and holiday themes</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckIcon color="success" fontSize="small" />
                  <Typography variant="body2">Purchase once, use forever</Typography>
                </Box>
              </Box>
              <Button
                variant="contained"
                size="large"
                onClick={() => trackAndNavigate('browse_themes', 'theme_store', '/themes')}
                startIcon={<ThemeIcon />}
              >
                Browse Themes
              </Button>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: 1,
                  p: 2,
                  bgcolor: 'white',
                  borderRadius: 2,
                  boxShadow: 1,
                }}
              >
                {/* Mini grid preview */}
                {[...Array(9)].map((_, i) => (
                  <Box
                    key={i}
                    sx={{
                      aspectRatio: '1/1',
                      borderRadius: 1,
                      background: i % 2 === 0
                        ? 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)'
                        : 'linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)',
                    }}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Container sx={{ mb: 8 }}>
        <Typography variant="h4" align="center" gutterBottom sx={{ mb: 4 }}>
          Features
        </Typography>
        <Grid container spacing={4}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Paper sx={{ p: 3, height: '100%', textAlign: 'center' }}>
              <GridIcon color="primary" sx={{ fontSize: 40, mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Easy Board Creation
              </Typography>
              <Typography color="text.secondary">
                Create sports squares boards for any sport in minutes. Set up teams, dates, and pricing with our simple wizard.
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Paper sx={{ p: 3, height: '100%', textAlign: 'center' }}>
              <SecureIcon color="primary" sx={{ fontSize: 40, mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Random Number Assignment
              </Typography>
              <Typography color="text.secondary">
                Fairly randomize the 0-9 numbers for home and away teams with our secure random number generator.
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Paper sx={{ p: 3, height: '100%', textAlign: 'center' }}>
              <TrophyIcon color="primary" sx={{ fontSize: 40, mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Flexible Payouts
              </Typography>
              <Typography color="text.secondary">
                Configure quarter payouts, reverse winners, and +5 rules to match your pool's rules.
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Paper sx={{ p: 3, height: '100%', textAlign: 'center' }}>
              <SportsIcon color="primary" sx={{ fontSize: 40, mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Real-time Scoring
              </Typography>
              <Typography color="text.secondary">
                Record scores and instantly see winning squares highlighted. Payouts are calculated automatically.
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Paper sx={{ p: 3, height: '100%', textAlign: 'center' }}>
              <CreditIcon color="primary" sx={{ fontSize: 40, mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Complete Ledger
              </Typography>
              <Typography color="text.secondary">
                Track who owes what and who has won. Record payments and keep everyone's balance up to date.
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Paper sx={{ p: 3, height: '100%', textAlign: 'center' }}>
              <PeopleIcon color="primary" sx={{ fontSize: 40, mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Easy Sharing
              </Typography>
              <Typography color="text.secondary">
                Share your board with an invite code. Players can claim squares from any device.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* How It Works */}
      <Container sx={{ mb: 8 }}>
        <Typography variant="h4" align="center" gutterBottom sx={{ mb: 4 }}>
          How It Works
        </Typography>
        <Grid container spacing={3} justifyContent="center">
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper sx={{ p: 3, textAlign: 'center', height: '100%' }}>
              <Typography variant="h3" color="primary" gutterBottom>
                1
              </Typography>
              <Typography variant="h6">Create</Typography>
              <Typography variant="body2" color="text.secondary">
                Set up your board with teams and payout rules
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper sx={{ p: 3, textAlign: 'center', height: '100%' }}>
              <Typography variant="h3" color="primary" gutterBottom>
                2
              </Typography>
              <Typography variant="h6">Invite</Typography>
              <Typography variant="body2" color="text.secondary">
                Share your invite code with friends
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper sx={{ p: 3, textAlign: 'center', height: '100%' }}>
              <Typography variant="h3" color="primary" gutterBottom>
                3
              </Typography>
              <Typography variant="h6">Play</Typography>
              <Typography variant="body2" color="text.secondary">
                Randomize numbers and lock the board
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper sx={{ p: 3, textAlign: 'center', height: '100%' }}>
              <Typography variant="h3" color="primary" gutterBottom>
                4
              </Typography>
              <Typography variant="h6">Win</Typography>
              <Typography variant="body2" color="text.secondary">
                Record scores and celebrate winners
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box
        sx={{
          textAlign: 'center',
          py: 6,
          px: 4,
          bgcolor: 'grey.100',
          borderRadius: 2,
        }}
      >
        <Typography variant="h4" gutterBottom>
          Ready to get started?
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Create your first board in minutes. No credit card required.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            size="large"
            onClick={() => {
              const destination = isAuthenticated ? '/create-board' : buildAuthUrl('/register', '/create-board');
              trackAndNavigate(isAuthenticated ? 'create_first_board' : 'sign_up_free', 'cta', destination);
            }}
          >
            {isAuthenticated ? 'Create Your First Board' : 'Sign Up Free'}
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={() => {
              const destination = isAuthenticated ? '/themes' : buildAuthUrl('/register', '/themes');
              trackAndNavigate('browse_themes', 'cta', destination);
            }}
            startIcon={<ThemeIcon />}
          >
            Browse Themes
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
